import { Component } from '@theme/component';
import {
  prefersReducedMotion,
  debounce,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  #pendingHighResImages = new WeakSet();

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll, true);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  #transitioning = false;
  #opening = null;

  async open(index, event) {
    event.preventDefault();
    const { dialog, media } = this.refs;
    if (this.#transitioning || dialog.open || !media[index]) return;
    this.#transitioning = true;
    try {
      dialog.showModal();
      this.selectThumbnail(index, { behavior: 'instant' });
      this.#opening = this.fadeDialog(0, 1);
      await this.#opening;
    } finally {
      this.#opening = null;
      this.#transitioning = false;
    }
  }

  async fadeDialog(from, to) {
    if (prefersReducedMotion()) return;
    const animation = this.refs.dialog.animate(
      [{ opacity: from }, { opacity: to }],
      { duration: 140, easing: 'ease-out', fill: 'none' }
    );
    try { await animation.finished; } catch { /* Detached or cancelled dialog. */ }
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  async loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!(image instanceof HTMLImageElement)) return false;

    const url = image.getAttribute('data_max_resolution');
    if (!url || this.#highResImagesLoaded.has(url) || this.#pendingHighResImages.has(image)) return false;
    this.#pendingHighResImages.add(image);

    try {
      const replacement = image.cloneNode(false);
      replacement.removeAttribute('srcset');
      replacement.removeAttribute('sizes');
      replacement.loading = 'eager';
      replacement.decoding = 'async';
      replacement.src = url;
      await replacement.decode();

      if (!image.isConnected ||
          mediaContainer.querySelector('img.product-media__image') !== image ||
          image.getAttribute('data_max_resolution') !== url) return false;

      replacement.className = image.className;
      replacement.style.cssText = image.style.cssText;
      image.replaceWith(replacement);
      this.#highResImagesLoaded.add(url);
      return true;
    } catch {
      return false;
    } finally {
      this.#pendingHighResImages.delete(image);
    }
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    if (!this.refs.dialog.open || this.#transitioning) return;
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    if (!this.refs.dialog.open || this.#transitioning) return;
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    if (this.#opening) await this.#opening;
    const { dialog, media } = this.refs;
    if (this.#transitioning || !dialog.open) return;
    this.#transitioning = true;
    try {
      const mostVisibleElement = await getMostVisibleElement(media);
      const gallery = this.closest('media-gallery');
      const mediaId = mostVisibleElement?.querySelector('[data-media-id]')?.dataset.mediaId;
      const slideshow = gallery?.slideshow;
      const slideIndex = slideshow?.slides?.findIndex(slide =>
        slide.querySelector('[data-media-id]')?.dataset.mediaId === mediaId
      );
      if (mediaId && slideIndex >= 0 && slideshow.current !== slideIndex) {
        await slideshow.select(slideIndex, undefined, { animate: false });
      }
      await this.fadeDialog(1, 0);
      this.closeDialog();
    } finally {
      this.#transitioning = false;
    }
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  if (!elements.length) return Promise.resolve(undefined);
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
